import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, CheckCircle, PlayCircle, Lock, ArrowLeft, ExternalLink, FileText, Video, X, AlertCircle, ChevronLeft, ChevronRight, HelpCircle, Award } from 'lucide-react';
import { getTrainingCourses, getCurriculumByCourse, updateOnboardStatus } from '../../../services/coachAPI';
import { getCourseQuizzes } from '../../../firebase/coachFirestore';

const getYoutubeEmbedUrl = (url = '') => {
  const value = String(url).trim();
  if (!value) return '';
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([^&#]+)/i,
    /(?:youtube\.com\/embed\/)([^&#?/]+)/i,
    /(?:youtu\.be\/)([^&#?/]+)/i
  ];
  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (match?.[1]) return `https://www.youtube.com/embed/${match[1]}`;
  }
  return '';
};

const isPdfUrl = (url = '') => {
  const u = String(url).toLowerCase();
  return u.endsWith('.pdf') || u.includes('.pdf?') || u.includes('%2fpdf');
};

const Step5TrainingCourses = ({ onNext, onBack, coachData }) => {
  const storedCoachUser = JSON.parse(localStorage.getItem('coachUser') || '{}');

  const isPhase5Completed =
    coachData?.onboardStatus === 'phase_5_completed' ||
    storedCoachUser?.onboardStatus === 'phase_5_completed';

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [curriculumItems, setCurriculumItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [viewedItems, setViewedItems] = useState({});
  const [expandedItem, setExpandedItem] = useState(null);
  const [resourceView, setResourceView] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [quizMode, setQuizMode] = useState(false);
  const [quizIntro, setQuizIntro] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizError, setQuizError] = useState('');

   const assignedCourseToCoach = storedCoachUser?.assignedCourses || [];

useEffect(() => {
  const fetchCourses = async () => {
    try {
      const data = await getTrainingCourses();

      if (Array.isArray(data) && data.length > 0) {
        const sorted = [...data].sort(
          (a, b) => (Number(a.order) || 0) - (Number(b.order) || 0)
        );

        const assignedCourseIds = assignedCourseToCoach.map((course) =>
          typeof course === "string" ? course : course.id || course.courseId
        );

        const filteredCourses = sorted.filter((course) =>
          assignedCourseIds.includes(course.id)
        );

        setCourses(filteredCourses);
      } else {
        setCourses([]);
      }
    } catch (err) {
      console.log("Failed to fetch courses", err);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  fetchCourses();
}, []);

  useEffect(() => {
    if (isPhase5Completed && courses.length > 0) {
      const allDone = {};
      courses.forEach((course) => {
        allDone[course.id] = true;
      });
      setViewedItems(allDone);
    }
  }, [isPhase5Completed, courses]);

  const loadCurriculumItems = useCallback(async (courseId) => {
    setLoadingItems(true);
    try {
      const data = await getCurriculumByCourse(courseId);
      setCurriculumItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setCurriculumItems([]);
    } finally {
      setLoadingItems(false);
    }
  }, []);

  const handleStartCourse = async (course) => {
    setSelectedCourse(course);
    setExpandedItem(null);
    setResourceView(null);
    await loadCurriculumItems(course.id);
  };

  const handleBackToCourses = () => {
    setSelectedCourse(null);
    setCurriculumItems([]);
    setExpandedItem(null);
    setResourceView(null);
    setQuizError('');
  };

  const updateLocalUserData = (status) => {
    ['coachUser', 'currentUser'].forEach((key) => {
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        parsed.onboardStatus = status;
        localStorage.setItem(key, JSON.stringify(parsed));
      }
    });
  };

  const allCourseItemsViewed = curriculumItems.length === 0 ||
    curriculumItems.every((item) => viewedItems[item.id]);

  const isQuizRequired = selectedCourse?.quizRequired === true;
  const passingScore = Number(selectedCourse?.passingScore) || 80;

  const handleMarkItemViewed = (itemId) => {
    if (isPhase5Completed) return;
    setViewedItems((prev) => ({ ...prev, [itemId]: true }));
  };

  const handleStartQuiz = async () => {
    setQuizLoading(true);
    setQuizError('');
    try {
      const data = await getCourseQuizzes(selectedCourse.id);
      if (!Array.isArray(data) || data.length === 0) {
        setQuizError('No quiz questions available for this course.');
        setQuizLoading(false);
        return;
      }
      setQuestions(data);
      setQuizMode(true);
      setQuizIntro(true);
      setCurrentQIndex(0);
      setAnswers({});
      setQuizSubmitted(false);
      setQuizScore(null);
    } catch (err) {
      setQuizError('Failed to load quiz questions. Please try again.');
    } finally {
      setQuizLoading(false);
    }
  };

  const handleSelectAnswer = (questionId, answer) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answer }));
  };

  const handleSubmitQuiz = () => {
    let correct = 0;
    const total = questions.length;
    questions.forEach((q) => {
      if (answers[q.id] === q.correctAns) {
        correct++;
      }
    });
    const percentage = total > 0 ? Math.round((correct / total) * 100) : 0;
    const passed = percentage >= passingScore;
    setQuizScore({ correct, total, percentage, passed });
    setQuizSubmitted(true);
  };

  const handleCompleteCourseAfterQuiz = () => {
    if (selectedCourse) {
      setViewedItems((prev) => ({ ...prev, [selectedCourse.id]: true }));
    }
    setQuizMode(false);
    setQuizIntro(false);
    setSelectedCourse(null);
    setQuestions([]);
    setAnswers({});
    setQuizSubmitted(false);
    setQuizScore(null);
    setCurrentQIndex(0);
    setQuizError('');
  };

  const handleRetryQuiz = () => {
    setQuizIntro(true);
    setAnswers({});
    setQuizSubmitted(false);
    setQuizScore(null);
    setCurrentQIndex(0);
  };

  const handleExitQuiz = () => {
    setQuizMode(false);
    setQuizIntro(false);
    setQuestions([]);
    setAnswers({});
    setQuizSubmitted(false);
    setQuizScore(null);
    setCurrentQIndex(0);
    setQuizError('');
  };

  const handleCompleteCourse = async () => {
    if (isPhase5Completed) return;
    setSaving(true);
    setError('');
    try {
      if (!allCourseItemsViewed) {
        setError('Please view all curriculum items before completing this course.');
        setSaving(false);
        return;
      }
      if (selectedCourse) {
        setViewedItems((prev) => ({ ...prev, [selectedCourse.id]: true }));
      }
      setSelectedCourse(null);
      setCurriculumItems([]);
      setExpandedItem(null);
    } catch (err) {
      setError(err.message || 'Failed to complete course.');
    } finally {
      setSaving(false);
    }
  };

  const handleFinish = async () => {
    setError('');
    const coachId = coachData?.id || coachData?.uid || coachData?._id;
    if (!coachId) {
      setError('Coach ID not found.');
      return;
    }
    if (isPhase5Completed) {
      onNext();
      return;
    }
    const allDone = courses.every((course) => viewedItems[course.id]);
    if (!allDone) {
      setError('Please complete all training courses before continuing.');
      return;
    }
    setSaving(true);
    try {
      await updateOnboardStatus(coachId, 'phase_5_completed');
      updateLocalUserData('phase_5_completed');
      onNext();
    } catch (err) {
      setError(err.message || 'Failed to save training progress.');
    } finally {
      setSaving(false);
    }
  };

  const completedCourseCount = courses.filter((c) => viewedItems[c.id]).length;
  const courseProgress = courses.length > 0 ? (completedCourseCount / courses.length) * 100 : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (selectedCourse && quizMode) {
    if (quizLoading) {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white" />
        </div>
      );
    }

    if (quizError) {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-3 sm:p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 sm:p-8 text-center animate-[scaleIn_0.2s_ease-out]">
            <div className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-amber-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Quiz Unavailable</h3>
            <p className="text-gray-500 text-sm mb-6">{quizError}</p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              {!isQuizRequired && (
                <button
                  type="button"
                  onClick={handleCompleteCourse}
                  disabled={saving}
                  className="px-5 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-2xl text-sm font-bold hover:from-emerald-700 hover:to-green-700 active:scale-[0.97] transition-all shadow-md"
                >
                  Mark Course as Complete
                </button>
              )}
              <button
                type="button"
                onClick={handleExitQuiz}
                className="px-5 py-3 border-2 border-gray-200 rounded-2xl text-sm font-bold text-gray-600 hover:border-gray-300 hover:bg-gray-50 active:scale-[0.97] transition-all"
              >
                Back to Courses
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (quizSubmitted && quizScore) {
      const isPassed = quizScore.passed;
      const circleColor = isPassed ? 'text-emerald-500' : 'text-red-400';
      const circleBg = isPassed ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200';

      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-3 sm:p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-[scaleIn_0.2s_ease-out]">
            <div className={`relative p-6 sm:p-8 text-center ${isPassed ? 'bg-gradient-to-b from-emerald-50 to-white' : 'bg-gradient-to-b from-red-50 to-white'}`}>
              <div className={`w-24 h-24 sm:w-28 sm:h-28 rounded-full ${circleBg} border-4 flex items-center justify-center mx-auto mb-5 shadow-lg ${isPassed ? 'shadow-emerald-200/50' : 'shadow-red-200/50'}`}>
                {isPassed ? (
                  <Award className="h-12 w-12 sm:h-14 sm:w-14 text-emerald-500" />
                ) : (
                  <X className="h-12 w-12 sm:h-14 sm:w-14 text-red-400" />
                )}
              </div>
              <h3 className={`text-2xl sm:text-3xl font-extrabold tracking-tight ${isPassed ? 'text-emerald-700' : 'text-red-600'}`}>
                {isPassed ? 'Congratulations!' : 'Not Quite'}
              </h3>
              <p className="text-gray-500 mt-1.5 text-sm sm:text-base">{isPassed ? 'You passed the quiz' : 'You didn\'t pass this time'}</p>
              <p className="text-gray-400 text-xs mt-2 font-medium">{selectedCourse.title}</p>

              <div className="mt-6 bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 shadow-sm">
                <div className="flex items-center justify-center gap-3 sm:gap-5">
                  <div className="text-center flex-1">
                    <p className={`text-3xl sm:text-4xl font-black ${isPassed ? 'text-emerald-600' : 'text-red-500'}`}>
                      {quizScore.percentage}%
                    </p>
                    <p className="text-[11px] font-medium text-gray-400 mt-1">Your Score</p>
                  </div>
                  <div className="w-px h-10 sm:h-12 bg-gray-200" />
                  <div className="text-center flex-1">
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">
                      {quizScore.correct}<span className="text-gray-400 text-base sm:text-lg font-normal">/{quizScore.total}</span>
                    </p>
                    <p className="text-[11px] font-medium text-gray-400 mt-1">Correct</p>
                  </div>
                  <div className="w-px h-10 sm:h-12 bg-gray-200" />
                  <div className="text-center flex-1">
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{passingScore}%</p>
                    <p className="text-[11px] font-medium text-gray-400 mt-1">To Pass</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6 flex flex-col sm:flex-row justify-center gap-3 bg-gray-50/80 border-t border-gray-100">
              {!isPassed && (
                <button
                  type="button"
                  onClick={handleRetryQuiz}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-2xl font-bold text-sm hover:bg-indigo-700 active:scale-[0.98] transition-all shadow-md shadow-indigo-200/50"
                >
                  <HelpCircle className="h-4 w-4" />
                  Try Again
                </button>
              )}
              {isPassed && (
                <button
                  type="button"
                  onClick={handleCompleteCourseAfterQuiz}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-2xl font-bold text-sm hover:from-emerald-700 hover:to-green-700 active:scale-[0.98] transition-all shadow-md shadow-emerald-200/50"
                >
                  <CheckCircle className="h-4 w-4" />
                  Complete &amp; Continue
                </button>
              )}
              <button
                type="button"
                onClick={handleExitQuiz}
                className="flex items-center justify-center px-5 py-3 border-2 border-gray-200 rounded-2xl text-sm font-bold text-gray-600 hover:border-gray-300 hover:bg-white active:scale-[0.98] transition-all"
              >
                Back to Courses
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (questions.length === 0) {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-3 sm:p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 sm:p-8 text-center animate-[scaleIn_0.2s_ease-out]">
            <div className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-amber-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">No Questions</h3>
            <p className="text-gray-500 text-sm mb-6">This course has no quiz questions yet.</p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              {!isQuizRequired && (
                <button
                  type="button"
                  onClick={handleCompleteCourse}
                  disabled={saving}
                  className="px-5 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-2xl text-sm font-bold hover:from-emerald-700 hover:to-green-700 active:scale-[0.97] transition-all shadow-md"
                >
                  Mark Course as Complete
                </button>
              )}
              <button
                type="button"
                onClick={handleExitQuiz}
                className="px-5 py-3 border-2 border-gray-200 rounded-2xl text-sm font-bold text-gray-600 hover:border-gray-300 hover:bg-gray-50 active:scale-[0.97] transition-all"
              >
                Back to Courses
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (quizIntro) {
      const questionCount = questions.length;
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-3 sm:p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-[scaleIn_0.2s_ease-out]">
            <div className="relative bg-gradient-to-br from-indigo-600 via-indigo-500 to-purple-600 px-6 sm:px-8 pt-8 sm:pt-10 pb-10 sm:pb-12 text-center overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/5" />
              <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-white/5" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-56 h-56 rounded-full bg-white/[0.03]" />
              <div className="relative">
                <div className="w-16 h-16 sm:w-18 sm:h-18 rounded-2xl bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center mx-auto mb-5 shadow-lg shadow-indigo-900/20">
                  <Award className="h-8 w-8 sm:h-9 sm:w-9 text-white" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-2 tracking-tight">Knowledge Check</h2>
                <p className="text-indigo-200 text-sm sm:text-base font-medium">{selectedCourse.title}</p>
                <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/10 text-xs font-semibold text-indigo-100">
                  <HelpCircle className="h-3.5 w-3.5" />
                  {questionCount} Question{questionCount !== 1 ? 's' : ''}
                </div>
              </div>
            </div>

            <div className="p-5 sm:p-7 space-y-4">
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/70 rounded-2xl p-4">
                <div className="flex items-start gap-3.5">
                  <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-amber-900">All questions are mandatory</p>
                    <p className="text-xs sm:text-sm text-amber-700 mt-1 leading-relaxed">
                      You must answer every question. Unanswered ones count as incorrect.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gradient-to-b from-gray-50 to-white border border-gray-200/80 rounded-2xl p-4 text-center">
                  <p className="text-2xl sm:text-3xl font-black text-gray-900">{questionCount}</p>
                  <p className="text-xs font-medium text-gray-400 mt-1">Questions</p>
                </div>
                <div className="bg-gradient-to-b from-emerald-50 to-white border border-emerald-200/80 rounded-2xl p-4 text-center">
                  <p className="text-2xl sm:text-3xl font-black text-emerald-600">{passingScore}%</p>
                  <p className="text-xs font-medium text-emerald-500 mt-1">Passing Score</p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100/80 rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <HelpCircle className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-indigo-900">Quick Tips</p>
                    <p className="text-xs sm:text-sm text-indigo-700 mt-1 leading-relaxed">
                      Navigate freely between questions using the progress bar. You can review and change 
                      your answers before submitting.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-5 sm:px-7 pb-5 sm:pb-7 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleExitQuiz}
                className="order-2 sm:order-1 flex-1 px-5 py-3.5 border-2 border-gray-200 rounded-2xl text-sm font-bold text-gray-600 hover:border-gray-300 hover:bg-gray-50 active:scale-[0.98] transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setQuizIntro(false)}
                className="order-1 sm:order-2 flex-1 px-5 py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl text-sm font-bold hover:from-indigo-700 hover:to-purple-700 active:scale-[0.98] transition-all shadow-lg shadow-indigo-200/50"
              >
                Start Quiz
              </button>
            </div>
          </div>
        </div>
      );
    }

    const currentQuestion = questions[currentQIndex];
    const isTrueFalse = currentQuestion.type === 'true/false';
    const optionLabels = isTrueFalse
      ? [{ key: 'True', label: 'A', value: currentQuestion.optionA }, { key: 'False', label: 'B', value: currentQuestion.optionB }]
      : ['A', 'B', 'C', 'D'].map((l) => ({ key: l, label: l, value: currentQuestion[`option${l}`] }));

    const optionColors = { A: 'from-blue-500 to-blue-600', B: 'from-emerald-500 to-emerald-600', C: 'from-orange-500 to-orange-600', D: 'from-violet-500 to-violet-600' };
    const optionBgColors = { A: 'bg-blue-50 border-blue-200', B: 'bg-emerald-50 border-emerald-200', C: 'bg-orange-50 border-orange-200', D: 'bg-violet-50 border-violet-200' };
    const optionRingColors = { A: 'ring-blue-200', B: 'ring-emerald-200', C: 'ring-orange-200', D: 'ring-violet-200' };
    const answeredCount = Object.keys(answers).length;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-3 sm:p-4">
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-[scaleIn_0.2s_ease-out]">
          <div className="flex items-center justify-between px-4 sm:px-6 pt-4 sm:pt-6 pb-4 border-b border-gray-100">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex-shrink-0 shadow-sm">
                <HelpCircle className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-base sm:text-lg font-bold text-gray-900 truncate">{selectedCourse.title}</h2>
                <p className="text-xs text-gray-400">Knowledge Check</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleExitQuiz}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all flex-shrink-0"
              title="Close quiz"
            >
              <X size={20} />
            </button>
          </div>

          <div className="px-4 sm:px-6 pt-4 sm:pt-5 pb-2 sm:pb-3">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                Question {currentQIndex + 1} of {questions.length}
              </span>
              <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full">
                {answeredCount} of {questions.length} answered
              </span>
            </div>
            <div className="flex gap-1.5">
              {questions.map((q, idx) => (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => setCurrentQIndex(idx)}
                  className={`flex-1 h-2.5 rounded-full transition-all ${
                    idx === currentQIndex
                      ? 'bg-indigo-600 shadow-sm shadow-indigo-200 scale-y-125'
                      : answers[q.id]
                      ? 'bg-emerald-500'
                      : 'bg-gray-200 hover:bg-gray-300'
                  }`}
                  title={`Question ${idx + 1}${answers[q.id] ? ' (answered)' : ''}`}
                />
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-3 sm:py-4">
            <div className="mb-3">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold tracking-wide ${
                isTrueFalse ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'
              }`}>
                {isTrueFalse ? 'True / False' : 'Multiple Choice'}
              </span>
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 leading-relaxed sm:leading-relaxed mb-5 sm:mb-6">
              {currentQuestion.question}
            </h3>
            <div className="space-y-3">
              {optionLabels.filter((o) => o.value).map((opt) => {
                const isSelected = answers[currentQuestion.id] === opt.key;
                const colors = optionColors[opt.label] || 'from-gray-500 to-gray-600';
                const bgColors = optionBgColors[opt.label] || 'bg-gray-50 border-gray-200';
                const ringColors = optionRingColors[opt.label] || 'ring-gray-200';
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => handleSelectAnswer(currentQuestion.id, opt.key)}
                    className={`w-full text-left group relative overflow-hidden rounded-2xl border-2 transition-all ${
                      isSelected
                        ? `${bgColors} ring-2 ${ringColors} shadow-lg scale-[1.01]`
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-md hover:-translate-y-0.5 active:scale-[0.99]'
                    }`}
                  >
                    <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4">
                      <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center font-bold text-sm sm:text-base text-white bg-gradient-to-br ${colors} ${
                        isSelected ? 'shadow-md' : 'opacity-70 group-hover:opacity-100'
                      } transition-all`}>
                        {opt.label}
                      </div>
                      <span className={`flex-1 text-sm sm:text-base leading-snug ${
                        isSelected ? 'font-bold text-gray-900' : 'font-medium text-gray-700'
                      }`}>
                        {opt.value}
                      </span>
                      {isSelected && (
                        <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                          <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-100 bg-gray-50/80 rounded-b-3xl flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setCurrentQIndex((prev) => Math.max(0, prev - 1))}
              disabled={currentQIndex === 0}
              className="inline-flex items-center gap-1.5 px-4 py-3 border-2 border-gray-200 rounded-2xl text-sm font-bold text-gray-600 hover:border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98] transition-all"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Previous</span>
            </button>

            <div className="flex items-center gap-2">
              {currentQIndex < questions.length - 1 ? (
                <button
                  type="button"
                  onClick={() => setCurrentQIndex((prev) => prev + 1)}
                  className="inline-flex items-center gap-1.5 px-5 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl text-sm font-bold hover:from-indigo-700 hover:to-purple-700 active:scale-[0.98] transition-all shadow-md shadow-indigo-200/50"
                >
                  <span className="hidden sm:inline">Next</span>
                  <span className="sm:hidden">Next</span>
                  <ChevronRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmitQuiz}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-2xl text-sm font-bold hover:from-emerald-700 hover:to-green-700 active:scale-[0.98] transition-all shadow-lg shadow-emerald-200/50"
                >
                  <CheckCircle className="h-4 w-4" />
                  Submit Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (selectedCourse) {
    return (
      <div className="space-y-5 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={handleBackToCourses}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-xl transition-colors flex-shrink-0"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl shadow-sm flex-shrink-0">
              <BookOpen className="h-6 w-6 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">{selectedCourse.title}</h2>
              <p className="text-sm text-gray-500 truncate">{selectedCourse.description}</p>
            </div>
          </div>
          {selectedCourse.isRequired && (
            <span className="inline-flex items-center self-start sm:self-center px-3 py-1 rounded-full text-xs font-bold bg-red-50 text-red-600 border border-red-100">Required</span>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">{error}</div>
        )}

        {loadingItems ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        ) : curriculumItems.length === 0 ? (
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-8 sm:p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center mx-auto mb-4">
              <BookOpen className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-gray-500 font-medium">No curriculum items for this course.</p>
            <div className="mt-5">
              {isQuizRequired ? (
                <button
                  type="button"
                  onClick={handleStartQuiz}
                  disabled={quizLoading}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl text-sm font-bold hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 active:scale-[0.97] transition-all shadow-md shadow-indigo-200/50"
                >
                  {quizLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  ) : (
                    <PlayCircle className="h-5 w-5" />
                  )}
                  {quizLoading ? 'Loading...' : 'Take Quiz'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleCompleteCourse}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl text-sm font-bold hover:from-emerald-700 hover:to-green-700 disabled:opacity-50 active:scale-[0.97] transition-all shadow-md shadow-emerald-200/50"
                >
                  {saving ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  ) : (
                    <CheckCircle className="h-5 w-5" />
                  )}
                  {saving ? 'Saving...' : 'Mark Course as Complete'}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-500"
                  style={{ width: `${(Object.keys(viewedItems).filter((id) => curriculumItems.some((i) => i.id === id)).length / curriculumItems.length) * 100}%` }}
                />
              </div>
              <span className="text-xs font-bold text-gray-500 whitespace-nowrap">
                {Object.keys(viewedItems).filter((id) => curriculumItems.some((i) => i.id === id)).length}/{curriculumItems.length}
              </span>
            </div>
            {curriculumItems.map((item) => {
              const isViewed = viewedItems[item.id];
              const isExpanded = expandedItem === item.id;
              const youtubeEmbed = getYoutubeEmbedUrl(item.youtubeLink);
              const hasPdf = item.attachmentUrl && isPdfUrl(item.attachmentUrl);

              return (
                <div
                  key={item.id}
                  className={`rounded-2xl border-2 transition-all ${
                    isViewed ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200' : 'bg-white border-gray-100 hover:border-gray-200'
                  }`}
                >
                  <div className="p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-3 sm:gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {item.youtubeLink && <Video size={14} className="text-red-400 flex-shrink-0" />}
                          {hasPdf && <FileText size={14} className="text-blue-400 flex-shrink-0" />}
                          <h3 className="font-bold text-gray-900 truncate text-sm sm:text-base">{item.title}</h3>
                          {isViewed && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 flex-shrink-0">
                              <CheckCircle className="h-3 w-3" />
                              Done
                            </span>
                          )}
                        </div>
                        {item.shortDescription && (
                          <p className="text-sm text-gray-600 mt-1">{item.shortDescription}</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setExpandedItem(isExpanded ? null : item.id)}
                        className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-indigo-600 bg-indigo-50 rounded-xl hover:bg-indigo-100 active:scale-[0.95] transition-all whitespace-nowrap flex-shrink-0"
                      >
                        {isExpanded ? 'Hide' : 'View'}
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-gray-200 p-4 sm:p-5 space-y-4">
                      {youtubeEmbed && (
                        <div className="aspect-video rounded-xl overflow-hidden bg-black shadow-sm">
                          <iframe
                            src={youtubeEmbed}
                            title={item.title}
                            className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      )}

                      {hasPdf && (
                        <div className="rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm">
                          <iframe
                            src={item.attachmentUrl}
                            title={item.attachmentName || 'PDF'}
                            className="w-full h-72 sm:h-96"
                          />
                        </div>
                      )}

                      {Array.isArray(item.resourceLinks) && item.resourceLinks.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Resources</p>
                          <div className="flex flex-wrap gap-2">
                            {item.resourceLinks.map((link, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => setResourceView(resourceView === link.url ? null : link.url)}
                                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold bg-blue-50 text-blue-700 rounded-xl hover:bg-blue-100 active:scale-[0.95] transition-all"
                              >
                                <ExternalLink size={12} />
                                {link.label || 'Open Link'}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {resourceView && (
                        <div className="rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm">
                          <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                            <p className="text-xs text-gray-500 truncate">{resourceView}</p>
                            <button
                              type="button"
                              onClick={() => setResourceView(null)}
                              className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-200"
                            >
                              <X size={14} />
                            </button>
                          </div>
                          <iframe
                            src={resourceView}
                            title="Resource"
                            className="w-full h-72 sm:h-96"
                            sandbox="allow-scripts allow-same-origin"
                          />
                        </div>
                      )}

                      {!isViewed && (
                        <button
                          type="button"
                          onClick={() => handleMarkItemViewed(item.id)}
                          disabled={saving}
                          className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl text-sm font-bold hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 active:scale-[0.98] transition-all shadow-md"
                        >
                          Mark as Viewed
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleBackToCourses}
            disabled={saving}
            className="w-full sm:w-auto px-6 py-3.5 border-2 border-gray-200 rounded-2xl text-gray-700 font-bold hover:border-gray-300 hover:bg-gray-50 disabled:opacity-50 active:scale-[0.98] transition-all"
          >
            Back to Courses
          </button>
          {!isPhase5Completed && allCourseItemsViewed && isQuizRequired && (
            <button
              type="button"
              onClick={handleStartQuiz}
              disabled={quizLoading}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl font-bold hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 active:scale-[0.98] shadow-lg shadow-indigo-200/50 transition-all"
            >
              {quizLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <PlayCircle className="h-5 w-5" />
              )}
              {quizLoading ? 'Loading...' : 'Take Quiz'}
            </button>
          )}
          {!isPhase5Completed && allCourseItemsViewed && !isQuizRequired && (
            <button
              type="button"
              onClick={handleCompleteCourse}
              disabled={saving}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-2xl font-bold hover:from-emerald-700 hover:to-green-700 disabled:opacity-50 active:scale-[0.98] shadow-lg shadow-emerald-200/50 transition-all"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <CheckCircle className="h-5 w-5" />
              )}
              {saving ? 'Saving...' : 'Mark Course as Complete'}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      <div className="flex items-center gap-3 pb-4 border-b">
        <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg shadow-sm">
          <BookOpen className="h-6 w-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Step 5: Training Courses</h2>
          <p className="text-sm text-gray-500">Complete all required training courses to continue</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">{error}</div>
      )}

      {isPhase5Completed && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-700">
          Training Courses already completed. Click Next to continue.
        </div>
      )}

      {courses.length === 0 ? (
  <div className="p-6 sm:p-8 bg-gray-50 border border-gray-200 rounded-2xl text-center">
    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center mx-auto mb-4">
      <BookOpen className="h-7 w-7 text-gray-400" />
    </div>
    <p className="text-gray-600 font-medium">No training courses assigned to you yet.</p>
  </div>
) : (
  <div className="space-y-3">
    {courses.map((course, index) => {
      const isDone = viewedItems[course.id];

      const prevCoursesDone =
        index === 0 ||
        courses.slice(0, index).every((c) => viewedItems[c.id]);

      const isLocked = !isDone && !prevCoursesDone;

      return (
        <div
          key={course.id}
          className={`p-4 sm:p-5 rounded-2xl border-2 transition-all ${
            isDone
              ? "bg-gradient-to-r from-green-50 to-emerald-50 border-green-200"
              : isLocked
              ? "bg-gray-50 border-gray-100 opacity-60"
              : "bg-white border-gray-100 hover:border-indigo-200 hover:shadow-md active:scale-[0.99]"
          }`}
        >
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className={`font-bold ${isDone ? 'text-gray-900' : 'text-gray-900'}`}>
                  {course.title}
                </h3>

                {isDone ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-700">
                    <CheckCircle className="h-3 w-3" />
                    Done
                  </span>
                ) : isLocked ? (
                  <Lock className="h-4 w-4 text-gray-300 flex-shrink-0" />
                ) : null}

                {course.isRequired && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-red-50 text-red-600 border border-red-100">
                    Required
                  </span>
                )}
              </div>

              <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">
                {course.description}
              </p>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
                {course.quizRequired && !isDone && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600">
                    <HelpCircle className="h-3.5 w-3.5" />
                    Quiz required
                  </span>
                )}

                {course.questionCount ? (
                  <span className="text-xs text-gray-400 font-medium">
                    {course.questionCount} questions · Pass: {course.passingScore || "N/A"}%
                  </span>
                ) : null}
              </div>

              {isLocked && (
                <p className="text-xs font-medium text-amber-600 mt-2 flex items-center gap-1">
                  <Lock className="h-3 w-3" />
                  Complete the previous course first
                </p>
              )}
            </div>

            <div className="flex-shrink-0 self-start sm:self-end w-full sm:w-auto">
              {!isDone && !isPhase5Completed && !isLocked && (
                <button
                  type="button"
                  onClick={() => handleStartCourse(course)}
                  disabled={saving}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl text-sm font-bold hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 active:scale-[0.97] transition-all shadow-md shadow-indigo-200/50"
                >
                  <PlayCircle className="h-4 w-4" />
                  Start
                </button>
              )}

              {isDone && (
                <span className="flex items-center justify-center gap-1.5 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-xl text-sm font-bold w-full sm:w-auto">
                  <CheckCircle className="h-4 w-4" />
                  Complete
                </span>
              )}
            </div>
          </div>
        </div>
      );
    })}
  </div>
)}

      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/70 rounded-2xl p-4 sm:p-5">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-bold text-blue-800">
            <span className="text-lg">{completedCourseCount}</span> of {courses.length} courses completed
          </p>
          <span className="text-xs font-bold text-blue-600 bg-blue-100 px-2.5 py-1 rounded-full">
            {Math.round(courseProgress)}%
          </span>
        </div>
        <div className="w-full bg-blue-200/60 rounded-full h-2.5 overflow-hidden">
          <div
            className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2.5 rounded-full transition-all duration-700 shadow-sm"
            style={{ width: `${courseProgress}%` }}
          />
        </div>
      </div>

      <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-4">
        <button
          type="button"
          onClick={onBack}
          disabled={saving}
          className="w-full sm:w-auto px-6 py-3.5 border-2 border-gray-200 rounded-2xl text-gray-700 font-bold hover:border-gray-300 hover:bg-gray-50 disabled:opacity-50 active:scale-[0.98] transition-all"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleFinish}
          disabled={saving || (!isPhase5Completed && completedCourseCount !== courses.length)}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl font-bold hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 active:scale-[0.98] shadow-lg transition-all"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
          ) : (
            <CheckCircle className="h-5 w-5" />
          )}
          {saving ? 'Saving...' : isPhase5Completed ? 'Next' : 'All Courses Complete — Continue'}
        </button>
      </div>
    </div>
  );
};

export default Step5TrainingCourses;
